import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";

const BEFORE = "/media/before.jpg";
const AFTER = "/media/after.jpg";

export default function BeforeAfter() {
  const ref = useRef(null);
  const [pos, setPos] = useState(55);

  const update = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    setPos(Math.min(96, Math.max(4, ((clientX - rect.left) / rect.width) * 100)));
  };

  return (
    <section id="transformation" data-testid="transformation-section" className="py-20 sm:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 48 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mb-14 max-w-3xl"
        >
          <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-[#FFB703]">03 // Proof of Craft</p>
          <h2 className="font-display text-[clamp(1.25rem,5.4vw,2.25rem)] font-extrabold uppercase tracking-tight text-white sm:text-4xl lg:text-5xl">
            The Optical <span className="text-[#FFB703]">Transformation</span>
          </h2>
          <p className="mt-5 text-sm leading-relaxed text-slate-400 sm:text-base">
            Drag the blade to compare daily-driver grime against the Elite Mobile Detail finish.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          ref={ref}
          data-testid="before-after-slider"
          onPointerDown={(e) => {
            ref.current.setPointerCapture(e.pointerId);
            update(e.clientX);
          }}
          onPointerMove={(e) => {
            if (e.buttons === 1) update(e.clientX);
          }}
          className="relative mx-auto aspect-[4/3] w-full max-w-3xl cursor-ew-resize touch-none select-none overflow-hidden rounded-2xl border border-white/10 shadow-[0_0_60px_rgba(0,0,0,0.6)]"
        >
          <img src={BEFORE} alt="Spotless interior detailed by Elite Mobile Detail" draggable={false} className="absolute inset-0 h-full w-full object-cover" />
          <img
            src={AFTER}
            alt="Crumbs and debris across the interior seats and carpet"
            draggable={false}
            className="absolute inset-0 h-full w-full object-cover"
            style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
          />
          <span className="absolute right-4 top-4 rounded-full bg-[#090A0E]/70 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400 backdrop-blur-md">
            Before
          </span>
          <span className="absolute left-4 top-4 rounded-full bg-[#FFB703] px-4 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-[#090A0E]">
            After — Interior Detail
          </span>
          <div data-testid="before-after-handle" className="absolute inset-y-0 z-10" style={{ left: `${pos}%` }}>
            <div className="absolute inset-y-0 -translate-x-1/2 border-l-2 border-dashed border-[#FFB703]" />
            <div className="absolute top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-[#FFB703]/60 bg-[#090A0E]/85 shadow-[0_0_24px_rgba(255,183,3,0.35)] backdrop-blur-md">
              <MoveHorizontal className="h-5 w-5 text-[#FFB703]" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
